/**
 * Utility functions for managing persistence data across logout/login cycles
 */

/**
 * Get all persistence-related localStorage keys that should be preserved
 */
export function getPersistenceKeys(): string[] {
    const persistenceKeys: string[] = []

    if (typeof window === 'undefined') return persistenceKeys

    // Get all localStorage keys
    const allKeys = Object.keys(localStorage)

    // Filter for persistence-related keys
    for (const key of allKeys) {
        // Table state keys (format: {persistenceKey}-{stateType})
        if (key.includes('-sorting') ||
            key.includes('-columnFilters') ||
            key.includes('-columnVisibility') ||
            key.includes('-columnOrder') ||
            key.includes('-columnPinning') ||
            key.includes('-globalFilter') ||
            key.includes('-rowSelection') ||
            key.includes('-presets') ||
            key.includes('-search-history')) {
            persistenceKeys.push(key)
        }

        // Direct persistence keys (table names)
        const tableKeys = [
            'activation-table',
            'activate-utilities-table',
            'acquisitions-table',
            'spikes-table',
            'homes-table',
            'missing-services-table',
            'active-bills-table',
            'bill-back-table',
            'payables-review',
            'payables-flagged',
            'payables-approved',
            'receivables-flagged',
            'receivables-draft',
            'receivables-approved',
            'table-filter-presets',
            'search-history'
        ]

        if (tableKeys.some(tableKey => key.startsWith(tableKey))) {
            persistenceKeys.push(key)
        }
    }

    return [...new Set(persistenceKeys)] // Remove duplicates
}

/**
 * Backup persistence data before logout
 */
export function backupPersistenceData(): Record<string, string> {
    const persistenceKeys = getPersistenceKeys()
    const backup: Record<string, string> = {}

    for (const key of persistenceKeys) {
        const value = localStorage.getItem(key)
        if (value !== null) {
            backup[key] = value
        }
    }

    return backup
}

/**
 * Restore persistence data after login
 */
export function restorePersistenceData(backup: Record<string, string>): void {
    if (typeof window === 'undefined') return

    for (const [key, value] of Object.entries(backup)) {
        try {
            localStorage.setItem(key, value)
        } catch (error) {
            console.warn(`Failed to restore persistence data for key: ${key}`, error)
        }
    }
}

/**
 * Clear only auth-related localStorage items, preserving persistence data
 */
export function clearAuthDataOnly(): void {
    if (typeof window === 'undefined') return

    // Backup persistence data
    const persistenceBackup = backupPersistenceData()

    // Clear all localStorage
    localStorage.clear()

    // Restore persistence data
    restorePersistenceData(persistenceBackup)

    // Clear sessionStorage (this is fine to clear completely)
    sessionStorage.clear()
}

/**
 * Enhanced logout that preserves persistence data across user sessions
 * This creates a more robust backup that can survive different user logins
 */
export function logoutWithPersistencePreservation(): void {
    if (typeof window === 'undefined') return

    // Backup persistence data to a special key that won't be cleared
    const persistenceBackup = backupPersistenceData()

    // Store the backup with a timestamp
    const backupKey = 'airbills_persistence_backup'
    const backupData = {
        timestamp: Date.now(),
        data: persistenceBackup
    }

    try {
        localStorage.setItem(backupKey, JSON.stringify(backupData))
    } catch (error) {
        console.warn('Failed to backup persistence data:', error)
    }

    // Clear all localStorage except our backup
    const keysToRemove = Object.keys(localStorage).filter(key => key !== backupKey)
    keysToRemove.forEach(key => localStorage.removeItem(key))

    // Clear sessionStorage
    sessionStorage.clear()
}

/**
 * Restore persistence data on login/app initialization
 * This should be called when the app initializes or user logs in
 */
export function restorePersistenceOnLogin(): void {
    if (typeof window === 'undefined') return

    const backupKey = 'airbills_persistence_backup'
    const backupDataStr = localStorage.getItem(backupKey)

    if (!backupDataStr) return

    try {
        const backupData = JSON.parse(backupDataStr)

        // Check if backup is not too old (e.g., 30 days)
        const maxAge = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds
        if (Date.now() - backupData.timestamp > maxAge) {
            localStorage.removeItem(backupKey)
            return
        }

        // Restore the persistence data
        restorePersistenceData(backupData.data)

        // Remove the backup key after successful restore
        localStorage.removeItem(backupKey)

    } catch (error) {
        console.warn('Failed to restore persistence data:', error)
        // Clean up corrupted backup
        localStorage.removeItem(backupKey)
    }
}
